import { AUTH_TOKENS_UPDATED_EVENT, clearStoredTokens, setStoredTokens } from './authTokens'
import { getCloudbaseTokens, refreshCloudbaseSession } from './cloudbase'

export type ApiOk<T> = { code: number; message?: string; data?: T }

export class ApiError extends Error {
  code?: number
  constructor(message: string, code?: number) {
    super(message)
    this.name = 'ApiError'
    this.code = code
  }
}

export class AuthRequiredError extends Error {
  constructor(message = 'AUTH_REQUIRED') {
    super(message)
    this.name = 'AuthRequiredError'
  }
}

export function isAuthRequiredError(e: unknown): e is AuthRequiredError {
  return e instanceof AuthRequiredError || (typeof e === 'object' && !!e && (e as any).name === 'AuthRequiredError')
}

const DEFAULT_BASE = 'https://tx.clipflow.cn'
export const AUTH_EXPIRED_EVENT = 'auth_expired'
export const AUTH_EXPIRED_STORAGE_KEY = 'auth_expired_hint'
let lastAuthExpiredAt = 0

let authBlocked = false
let waitForLoginPromise: Promise<string> | null = null
let waitForLoginResolve: ((t: string) => void) | null = null
let waitForLoginReject: ((e: any) => void) | null = null
let waitForLoginTimer = 0

function beginWaitForLogin() {
  if (waitForLoginPromise) return waitForLoginPromise
  waitForLoginPromise = new Promise<string>((resolve, reject) => {
    waitForLoginResolve = resolve
    waitForLoginReject = reject
  })
  if (typeof window !== 'undefined') {
    if (waitForLoginTimer) window.clearTimeout(waitForLoginTimer)
    waitForLoginTimer = window.setTimeout(() => {
      const rej = waitForLoginReject
      waitForLoginPromise = null
      waitForLoginResolve = null
      waitForLoginReject = null
      waitForLoginTimer = 0
      if (rej) rej(new AuthRequiredError())
    }, 5 * 60_000)
  }
  return waitForLoginPromise
}

function resolveWaitForLogin(token: string) {
  const t = String(token || '').trim()
  if (!t) return
  authBlocked = false
  const res = waitForLoginResolve
  waitForLoginPromise = null
  waitForLoginResolve = null
  waitForLoginReject = null
  if (typeof window !== 'undefined' && waitForLoginTimer) {
    window.clearTimeout(waitForLoginTimer)
    waitForLoginTimer = 0
  }
  if (res) res(t)
}

function notifyAuthExpired(apiPath: string) {
  if (typeof window === 'undefined') return
  const now = Date.now()
  if (now - lastAuthExpiredAt < 1500) return
  lastAuthExpiredAt = now
  try {
    try {
      const routePath = `${window.location.pathname || '/'}${window.location.search || ''}${window.location.hash || ''}`
      window.localStorage.setItem(
        AUTH_EXPIRED_STORAGE_KEY,
        JSON.stringify({ at: now, path: routePath, apiPath: String(apiPath || '') }),
      )
    } catch {}
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT, { detail: { path: window.location.pathname || '/', apiPath } }))
  } catch {}
}

function getApiBase() {
  const envBase = (import.meta.env.VITE_API_BASE as string | undefined) || ''
  const baseRaw = envBase.trim() || DEFAULT_BASE

  const normalized = /^https?:\/\//.test(baseRaw) ? baseRaw.replace(/\/api\/?$/, '') : baseRaw
  return normalized.replace(/\/+$/, '')
}

function createNonce(): string {
  if (typeof crypto !== 'undefined' && 'getRandomValues' in crypto) {
    const buf = new Uint8Array(4)
    crypto.getRandomValues(buf)
    const n = Array.from(buf)
      .map((x) => x.toString(16).padStart(2, '0'))
      .join('')
    return n.slice(0, 6)
  }
  return String(Math.floor(Math.random() * 1_000_000)).padStart(6, '0')
}

function buildApiUrl(path: string) {
  const base = getApiBase()
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
  const url = new URL(`${base}${path}`, origin)
  url.searchParams.set('nonce', createNonce())
  url.searchParams.set('t', String(Math.floor(Date.now() / 1000)))
  return url.toString()
}

let refreshPromise: Promise<string> | null = null
async function refreshAccessTokenOnce() {
  if (refreshPromise) return refreshPromise
  refreshPromise = (async () => {
    try {
      const next: any = await refreshCloudbaseSession()
      const expiresAt =
        typeof next?.expiresAt === 'number' && Number.isFinite(next.expiresAt)
          ? Math.round(next.expiresAt)
          : typeof next?.expiresIn === 'number' && Number.isFinite(next.expiresIn)
            ? Date.now() + Math.max(0, Math.round(next.expiresIn * 1000))
            : undefined
      setStoredTokens({ accessToken: next.accessToken, refreshToken: next.refreshToken || '', accessTokenExpiresAt: expiresAt })
      return next.accessToken
    } catch {}

    const next: any = await getCloudbaseTokens()
    const expiresAt =
      typeof next?.expiresAt === 'number' && Number.isFinite(next.expiresAt)
        ? Math.round(next.expiresAt)
        : typeof next?.expiresIn === 'number' && Number.isFinite(next.expiresIn)
          ? Date.now() + Math.max(0, Math.round(next.expiresIn * 1000))
          : undefined
    setStoredTokens({ accessToken: next.accessToken, refreshToken: next.refreshToken || '', accessTokenExpiresAt: expiresAt })
    return next.accessToken
  })()
  try {
    return await refreshPromise
  } finally {
    refreshPromise = null
  }
}

if (typeof window !== 'undefined') {
  try {
    window.addEventListener(AUTH_TOKENS_UPDATED_EVENT, ((e: any) => {
      const t = String(e?.detail?.accessToken || '').trim()
      if (t) resolveWaitForLogin(t)
    }) as any)
  } catch {}
}

export async function apiRequest<T>(
  path: string,
  init: RequestInit & { token?: string; retry401?: boolean; timeoutMs?: number; authExpiredMode?: 'notify' | 'silent' } = {},
): Promise<ApiOk<T>> {
  if (authBlocked && init.token) {
    if (init.authExpiredMode === 'silent') throw new AuthRequiredError()
    notifyAuthExpired(path)
    const t = await beginWaitForLogin()
    return await apiRequest<T>(path, { ...init, token: t, retry401: false })
  }

  const controller = new AbortController()
  const timeoutMs = Number(init.timeoutMs ?? import.meta.env.VITE_API_TIMEOUT_MS ?? 20000)
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs)

  try {
    const fetchInit = (() => {
      const anyInit = init as any
      const { token: _t, retry401: _r, timeoutMs: _m, ...rest } = anyInit || {}
      return rest as RequestInit
    })()

    const headers = new Headers(init.headers || {})
    if (init.token) headers.set('Authorization', `Bearer ${init.token}`)
    if (!headers.has('Accept')) headers.set('Accept', 'application/json')

    const res = await fetch(buildApiUrl(path), {
      ...fetchInit,
      headers,
      signal: controller.signal,
    })

    if (res.status === 401 && init.retry401 !== false) {
      try {
        const nextAccessToken = await refreshAccessTokenOnce()
        headers.set('Authorization', `Bearer ${nextAccessToken}`)
        return await apiRequest<T>(path, { ...init, headers, token: nextAccessToken, retry401: false })
      } catch {
        if (init.authExpiredMode === 'silent') throw new AuthRequiredError()
        authBlocked = true
        beginWaitForLogin()
        clearStoredTokens()
        notifyAuthExpired(path)
        const t = await beginWaitForLogin()
        headers.set('Authorization', `Bearer ${t}`)
        return await apiRequest<T>(path, { ...init, headers, token: t, retry401: false })
      }
    }

    const text = await res.text()
    let json: unknown = null
    try {
      json = text ? JSON.parse(text) : null
    } catch {
      json = null
    }

    if (res.ok) {
      const code = typeof json === 'object' && json && 'code' in json ? Number((json as any).code) : NaN
      if (code === 401) {
        if (init.authExpiredMode === 'silent') throw new AuthRequiredError()
        authBlocked = true
        beginWaitForLogin()
        clearStoredTokens()
        notifyAuthExpired(path)
        throw new AuthRequiredError()
      }
    }

    if (!res.ok) {
      if (res.status === 401) {
        if (init.authExpiredMode === 'silent') throw new AuthRequiredError()
        authBlocked = true
        beginWaitForLogin()
        notifyAuthExpired(path)
        throw new AuthRequiredError()
      }
      if (typeof json === 'object' && json && 'code' in json) {
        return json as ApiOk<T>
      }
      const msg =
        typeof json === 'object' && json && 'message' in json
          ? String((json as any).message)
          : `${res.status} ${res.statusText}`
      throw new ApiError(msg)
    }

    return (json as ApiOk<T>) || ({ code: 0 } as ApiOk<T>)
  } finally {
    window.clearTimeout(timeoutId)
  }
}

export function assertOk<T>(resp: ApiOk<T>, fallbackMsg: string): T {
  if (!resp) throw new ApiError(fallbackMsg)
  if (resp.code !== 0) throw new ApiError(resp.message || fallbackMsg, resp.code)
  if (resp.data === undefined) throw new ApiError(fallbackMsg, resp.code)
  return resp.data
}
